import { Component, OnInit, ViewChild } from '@angular/core';
import {
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { formatDate } from '@app/helpers/date.helper';
import { OtpService } from '@app/services/authenticators/otp.service';
import { CertificateService } from '@app/services/certificates/certificate.service';

import { Tokens } from '@app/store/models/certificate.model';
import { GlobalState } from '@app/store/store';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { chunk } from 'lodash-es';
import moment from 'moment';

import { ToastrService } from 'ngx-toastr';
import { LocalStorageService } from 'ngx-webstorage';
import { tap } from 'rxjs';
import { VirtualTokenFormComponent } from '../virtual-token-form/virtual-token-form.component';
@Component({
  selector: 'app-virtual-token',
  templateUrl: './virtual-token.component.html',
  styleUrls: ['./virtual-token.component.scss'],
  standalone: false,
})
@UntilDestroy()
export class VirtualTokenComponent implements OnInit {
  @ViewChild(VirtualTokenFormComponent)
  private tokenFormComponent?: VirtualTokenFormComponent;

  constructor(
    private certificateService: CertificateService,
    private otpService: OtpService,
    private toasterService: ToastrService,
    private translateService: TranslateService,
    private localStorageService: LocalStorageService,
    private store: Store<GlobalState>,
    private router: Router,
    private route: ActivatedRoute
  ) {}

  loading: boolean = false;
  reversed: boolean = false;
  tokenId: number = 0;
  submitMode: string = '';
  isTrissUser: boolean = false; //triss user
  error_code: string;
  currentPage: number = 1;
  itemsPerPage: number = 6;
  showSuccess: boolean = false;
  tokenName: string = '';
  successMode: string = '';
  // Chiave usata in sessionStorage per ricordare
  // se ho già fatto un reload a causa dell'errore MT_NTA_003 (evita loop)
  private readonly RELOAD_GUARD_KEY = 'VT_RELOAD_ONCE';

  _tokens: Tokens[] = [];

  ngOnInit(): void {
    this.checkSuccessMessage();
    this.getVirtualTokens();

    this.store
      .pipe(
        untilDestroyed(this),
        tap(store => {
          if (store.session) {
            this.isTrissUser = store.session?.trissUser;
          }
        })
      )
      .subscribe();
  }

  get tokens(): Tokens[] {
    return this._tokens;
  }

  get totalItems(): number {
    return this.tokens.length - 1 || 0;
  }

  get paginatedTokens(): Tokens[] {
    return chunk(this._tokens, this.itemsPerPage)[this.currentPage - 1];
  }

  public onPageChanged(data: any): void {
    console.log('onPageChanged', data);
    this.currentPage = data.currentPage;
  }

  orderTokens() {
    if (!this.reversed) {
      this._tokens.sort(
        (a, b) => <any>new Date(b.createdAt) - <any>new Date(a.createdAt)
      );
      this.reversed = true;
    } else {
      this._tokens.sort(
        (a, b) => <any>new Date(a.createdAt) - <any>new Date(b.createdAt)
      );
      this.reversed = false;
    }
  }

  otp = new UntypedFormControl('', [
    Validators.required,
    Validators.minLength(6),
    Validators.maxLength(8),
  ]);

  form = new UntypedFormGroup({
    otp: this.otp,
  });

  // convenience getter for easy access to form fields
  get f() {
    return this.form.controls;
  }

  get formErrors() {
    const errors = this.form.errors;
    return errors;
  }

  getVirtualTokens() {
    this.loading = true;
    this.certificateService
      .getVirtualTokens()
      .then(response => {
        this.loading = false;
        this._tokens = response || [];
      })
      .catch(error => {
        console.log(error);

        // recupero l'errorCode salvato in sessionStorage (dal service degli errori)
        const code = sessionStorage.getItem('errorCode');

        // verifico se ho già eseguito un reload per questo errore
        const alreadyReloaded =
          sessionStorage.getItem(this.RELOAD_GUARD_KEY) === '1';

        if (code === 'MT_NTA_003' && !alreadyReloaded) {
          // ✅ caso speciale: errore MT_NTA_003 intercettato per la prima volta
          // 1. chiudo eventuali toast aperti (non ha senso mostrarli se sto ricaricando)
          this.toasterService.clear();

          // 2. imposto il guard in sessione → evito di ricaricare ancora
          sessionStorage.setItem(this.RELOAD_GUARD_KEY, '1');

          // 3. ricarico la stessa pagina UNA SOLA VOLTA
          this.reloadSamePage();
        } else {
          // ❌ caso in cui l'errore MT_NTA_003 arriva di nuovo dopo il reload
          // Non devo ricaricare di nuovo, altrimenti vado in loop

          if (code === 'MT_NTA_003') {
            // chiudo i toast ma non faccio nulla di più
            this.toasterService.clear();
          }

          // Pulizia: rimuovo i marker per evitare comportamenti sporchi in seguito
          sessionStorage.removeItem('errorCode');
          sessionStorage.removeItem(this.RELOAD_GUARD_KEY);
        }

        this.loading = false;
      });
  }

  onSubmit() {
    this.loading = true;

    switch (this.submitMode) {
      case 'REVOKE':
        this.revokeToken(this.tokenId);
        break;
      case 'DOWNLOAD':
        this.downloadToken(this.tokenId);
        break;
    }
  }

  revokeToken(id: number) {
    this.certificateService
      .revokeVirtualToken(id, 'REVOKE', this.f['otp'].value)
      .subscribe({
        next: async response => {
          this.loading = false;

          this.showSuccessMessage('REVOKE');
          this.getVirtualTokens();
          this.closeModal();
        },
        error: error => {
          console.error(error);
          this.clearForm();
          this.loading = false;
        },
        complete: () => {
          this.loading = false;
        },
      });
  }

  downloadToken(id: number) {
    this.certificateService
      .downloadVirtualToken(id, this.f['otp'].value)
      .subscribe({
        next: async response => {
          this.loading = false;

          const blob = new Blob([response.value], { type: 'text/csv' });
          const data = window.URL.createObjectURL(blob);
          var link = document.createElement('a');
          link.href = data;
          link.download = 'Token n.' + this.tokenId + '.txt';

          // Necessario per Firefox
          link.dispatchEvent(
            new MouseEvent('click', {
              bubbles: true,
              cancelable: true,
              view: window,
            })
          );

          setTimeout(function () {
            // Necessario per Firefox
            window.URL.revokeObjectURL(data);
            link.remove();
          }, 100);

          this.toasterService.success(
            '',
            this.translateService.instant(
              'VIRTUAL_TOKEN_PAGE.DOWNLOAD_SUCCESS'
            ),
            {
              positionClass: 'toast-top-right',
            }
          );

          this.closeModal();
        },
        error: error => {
          console.error(error);
          this.clearForm();
          this.loading = false;
        },
        complete: () => {
          this.loading = false;
        },
      });
  }

  clearForm() {
    this.otpService.resetOtpInputVal(true);
    this.otp = new UntypedFormControl('', [
      Validators.required,
      Validators.minLength(6),
      Validators.maxLength(8),
    ]);

    this.form = new UntypedFormGroup({
      otp: this.otp,
    });
  }

  formatTokenDate(date: string) {
    return formatDate(moment(date).utc().local(), 'DD/MM/yyyy');
  }

  checkSuccessMessage() {
    let needsTokenSuccess =
      this.localStorageService.retrieve('needsTokenSuccess');
    if (needsTokenSuccess) {
      this.showSuccessMessage('GENERATE', needsTokenSuccess);
      this.localStorageService.clear('needsTokenSuccess');
    }
  }

  showSuccessMessage(mode: string, name?: string) {
    if (name) {
      this.tokenName = name;
    }
    this.successMode = mode;
    this.showSuccess = true;
  }

  public otpModalVisible = false;
  public tokenFormModalVisible = false;

  openModal(name: string = '', id?: number, tokenName?: string) {
    this.showSuccess = false;
    this.submitMode = name;
    if (id) {
      this.sendOtp();
      this.tokenId = id;
    }
    if (tokenName) {
      this.tokenName = tokenName;
    }

    if (name === 'TOKEN_FORM') {
      this.tokenFormComponent?.goToStepOne();
      this.tokenFormModalVisible = true;
    } else {
      this.otpModalVisible = true;
    }
  }

  onToggleOtpModal(visible: boolean): void {
    this.otpModalVisible = visible;
    if (!visible) {
      this.closeModal();
    }
  }

  onToggleTokenFormModal(visible: boolean): void {
    this.tokenFormModalVisible = visible;
    if (!visible) {
      this.closeModal();
    }
  }

  sendOtp() {
    this.otpService.generateCode().subscribe({
      next: async response => {
        // Se tutto va bene
      },
      error: error => {
        console.error(error);
      },
      complete: () => {},
    });
  }

  closeModal() {
    this.clearForm();
    this.otpModalVisible = false;
    this.tokenFormModalVisible = false;
    this.tokenFormComponent?.goToStepOne();
  }
  reloadSamePage() {
    const originalReuse = this.router.routeReuseStrategy.shouldReuseRoute;
    // disabilito temporaneamente il riuso delle route
    this.router.routeReuseStrategy.shouldReuseRoute = () => false;

    this.router
      .navigate([], {
        relativeTo: this.route,
        // query param "usa e getta" → forza Angular a ricaricare il componente
        queryParams: { _r: Date.now() },
        queryParamsHandling: 'merge',
      })
      .finally(() => {
        // ripristino la strategia originale per le prossime navigazioni
        this.router.routeReuseStrategy.shouldReuseRoute = originalReuse;
      });
  }
}
